import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { googleReviews, noteMoyenne, nbAvis } from "../testimonials";

const PAGE_SIZE = 12;

function avatarColor(letter) {
  const palette = ["#8a6420", "#b8892a", "#7a5a1c", "#a37525", "#c99a3a", "#6d4d18", "#96701f"];
  const idx = (letter.charCodeAt(0) - 65 + 26) % palette.length;
  return palette[idx];
}

export default function GoogleReviews() {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(googleReviews.length / PAGE_SIZE);
  const start = page * PAGE_SIZE;
  const items = googleReviews.slice(start, start + PAGE_SIZE);

  return (
    <section className="py-20 bg-charcoal">
      <div className="container-narrow">
        {/* Header Google style */}
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 gold-border rounded-full px-5 py-2 mb-5">
            <GoogleLogo />
            <span className="text-sm text-gold-soft tracking-wide">Avis Google vérifiés</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl gold-gradient-text">
            {nbAvis} personnes témoignent
          </h2>
          <div className="gold-divider my-5 mx-auto max-w-[220px]" />
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="font-display text-4xl text-gold">{noteMoyenne.toFixed(1)}</span>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={`avg-star-${i}`} className="w-5 h-5 text-gold" fill="currentColor" />
              ))}
            </div>
          </div>
          <p className="text-sm text-gold-soft/95">Basé sur {nbAvis} avis certifiés de consultants</p>
        </div>

        {/* Reviews grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {items.map((r) => (
            <article key={r.id} className="card-onyx rounded-xl p-5">
              <header className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center font-display text-lg"
                  style={{ background: avatarColor(r.initiale), color: "#0a0a0a" }}
                >
                  {r.initiale}
                </div>
                <div className="flex-1">
                  <div className="text-gold font-medium leading-tight">{r.nom}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(r.note)].map((_, k) => (
                        <Star key={`${r.id}-star-${k}`} className="w-3.5 h-3.5 text-gold" fill="currentColor" />
                      ))}
                    </div>
                    <span className="text-[11px] text-gold-deep">{r.il_y_a}</span>
                  </div>
                </div>
                <GoogleG />
              </header>
              <p className="text-sm text-gold-soft/95 leading-relaxed font-serif italic mb-2">«{r.texte}»</p>
              <div className="text-[11px] uppercase tracking-widest text-gold-deep">{r.ville}</div>
            </article>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-2 mt-10 flex-wrap">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="btn-ghost-gold rounded-md px-3 py-2 text-sm disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Préc.
          </button>
          <div className="flex gap-1 flex-wrap justify-center">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-btn-${i}`}
                onClick={() => setPage(i)}
                className={`w-9 h-9 rounded-md text-sm ${i === page ? "btn-gold" : "btn-ghost-gold"}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page === totalPages - 1}
            className="btn-ghost-gold rounded-md px-3 py-2 text-sm disabled:opacity-40 flex items-center gap-1"
          >
            Suiv. <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <p className="text-center text-xs text-gold-deep mt-6">Page {page + 1} sur {totalPages} — {nbAvis} avis au total</p>
      </div>
    </section>
  );
}

function GoogleLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" className="opacity-90">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
